/**
 * Infrastructure Column Configurations
 *
 * Defines data table column configurations for various infrastructure views:
 * - Virtual Infrastructure (vCenters, Hypervisors, VMs)
 * - Monitored Infrastructure (Servers, Network Devices)
 * - Infrastructure List Selectors
 */

import { capitalize, partial } from 'lodash';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import {
    DESCRIPTION_FIELD,
    DEVICE_TYPE_FIELD,
    LABEL_CLUSTER,
    LABEL_COLLECTOR,
    LABEL_DATA_CENTER,
    LABEL_DESCRIPTION,
    LABEL_HYPERVISOR,
    LABEL_IP_ADDRESS,
    LABEL_NAME,
    LABEL_POLLING_STATUS,
    LABEL_STATUS,
    LABEL_TYPE,
    LABEL_VCENTER,
    LIST_COLLECTORS_ID,
    MAPPING_CLUSTER_FIELD,
    MAPPING_DATACENTER_FIELD,
    MAPPING_HYPERVISORNAME_FIELD,
    MAPPING_VCENTER_FIELD,
    MONITORED_INFRASTRUCTURE_FUNCTION_NAME,
    NAME_FIELD,
    IPADDRESS_FIELD,
    PLCNAME_FIELD,
    SUBTYPE_FIELD,
    UISTATE_FIELD,
    VIRTUAL_INFRASTRUCTURE_FUNCTION_NAME,
} from './InfrastructureConstants';
import { AdminPages, buildPageTarget, buildPageTargetWithIdProp } from '../AdminLinkUtils';
import { serverDeviceTypeFormatter, serverSubTypeFormatter } from './InfrastructureFormatters';
import { classifierLogLinkRenderer, renderStatus } from './InfrastructureRenderers';

const COLLECTORS_FUNCTION_NAME = AdminPages.Collectors;

/**
 * Common column definitions shared across infrastructure tables
 */
const COMMON_COLUMNS = {
    // Name column for monitored infrastructure
    nameMonitored: {
        type: COLUMN_TYPES.DRILLINLINK,
        mapping: NAME_FIELD,
        title: LABEL_NAME,
        sortable: true,
        width: 235,
        link: {
            target: buildPageTarget(MONITORED_INFRASTRUCTURE_FUNCTION_NAME),
        },
    },
    // Name column for virtual infrastructure
    nameVirtual: {
        type: COLUMN_TYPES.DRILLINLINK,
        mapping: NAME_FIELD,
        title: LABEL_NAME,
        sortable: true,
        width: 220,
        link: {
            target: buildPageTarget(VIRTUAL_INFRASTRUCTURE_FUNCTION_NAME),
        },
    },
    // Name column for list selectors (wider)
    nameList: {
        type: COLUMN_TYPES.DRILLINLINK,
        mapping: NAME_FIELD,
        title: LABEL_NAME,
        sortable: true,
        width: 300,
        link: {
            target: buildPageTarget(VIRTUAL_INFRASTRUCTURE_FUNCTION_NAME),
        },
    },
    // IP Address column for monitored infrastructure
    ipAddressMonitored: {
        type: COLUMN_TYPES.DRILLINLINK,
        mapping: IPADDRESS_FIELD,
        title: LABEL_IP_ADDRESS,
        sortable: true,
        width: 120,
        link: {
            target: buildPageTarget(MONITORED_INFRASTRUCTURE_FUNCTION_NAME),
        },
        sortProperty: 'norm_ipAddress',
    },
    // IP Address column for virtual infrastructure
    ipAddressVirtual: {
        type: COLUMN_TYPES.DRILLINLINK,
        mapping: IPADDRESS_FIELD,
        title: LABEL_IP_ADDRESS,
        sortable: true,
        width: 120,
        link: {
            target: buildPageTarget(VIRTUAL_INFRASTRUCTURE_FUNCTION_NAME),
        },
        sortProperty: 'norm_ipAddress',
    },
    // Collector column (standard width)
    collector: {
        type: COLUMN_TYPES.DRILLINLINK,
        mapping: PLCNAME_FIELD,
        title: LABEL_COLLECTOR,
        sortable: true,
        width: 225,
        link: {
            target: buildPageTargetWithIdProp(COLLECTORS_FUNCTION_NAME, LIST_COLLECTORS_ID),
        },
    },
    // Collector column (narrower for virtual infrastructure)
    collectorNarrow: {
        type: COLUMN_TYPES.DRILLINLINK,
        mapping: PLCNAME_FIELD,
        title: LABEL_COLLECTOR,
        sortable: true,
        width: 150,
        link: {
            target: buildPageTargetWithIdProp(COLLECTORS_FUNCTION_NAME, LIST_COLLECTORS_ID),
        },
    },
    // Status column with capitalize formatter
    status: {
        type: COLUMN_TYPES.TEXT,
        mapping: UISTATE_FIELD,
        title: LABEL_STATUS,
        sortable: true,
        width: 100,
        formatter: capitalize,
        renderer: renderStatus,
    },
    // Description column
    description: {
        type: COLUMN_TYPES.TEXT,
        mapping: DESCRIPTION_FIELD,
        title: LABEL_DESCRIPTION,
        sortable: true,
        flex: 1,
    },
    // Device Type column (for monitored infrastructure)
    deviceType: {
        type: COLUMN_TYPES.TEXT,
        mapping: DEVICE_TYPE_FIELD,
        title: LABEL_TYPE,
        sortable: true,
        width: 145,
        formatter: serverDeviceTypeFormatter,
    },
    // SubType column (for virtual infrastructure)
    subType: {
        type: COLUMN_TYPES.TEXT,
        mapping: SUBTYPE_FIELD,
        title: LABEL_TYPE,
        sortable: true,
        width: 120,
        formatter: serverSubTypeFormatter,
    },
};

/**
 * Virtual infrastructure specific columns
 */
const VIRTUAL_SPECIFIC_COLUMNS = {
    vCenter: {
        type: COLUMN_TYPES.TEXT,
        mapping: MAPPING_VCENTER_FIELD,
        title: LABEL_VCENTER,
        sortable: true,
        width: 170,
    },
    dataCenter: {
        type: COLUMN_TYPES.TEXT,
        mapping: MAPPING_DATACENTER_FIELD,
        title: LABEL_DATA_CENTER,
        width: 120,
        sortable: true,
    },
    cluster: {
        type: COLUMN_TYPES.TEXT,
        mapping: MAPPING_CLUSTER_FIELD,
        title: LABEL_CLUSTER,
        width: 100,
        sortable: true,
    },
    hypervisor: {
        type: COLUMN_TYPES.TEXT,
        mapping: MAPPING_HYPERVISORNAME_FIELD,
        title: LABEL_HYPERVISOR,
        width: 220,
        sortable: true,
    },
};

/**
 * Base column configuration for monitored infrastructure tables.
 * Used as foundation for building monitored infrastructure columns.
 */
const MONITORED_INFRASTRUCTURE_COLUMNS_CONFIG = [
    COMMON_COLUMNS.nameMonitored,
    COMMON_COLUMNS.ipAddressMonitored,
    COMMON_COLUMNS.collector,
    COMMON_COLUMNS.deviceType,
    COMMON_COLUMNS.description,
];

const getPollingStatusColumn = (showClassifierLogDialog) => {
    return {
        type: COLUMN_TYPES.TEXT,
        mapping: UISTATE_FIELD,
        title: LABEL_POLLING_STATUS,
        sortable: true,
        width: 150,
        formatter: capitalize,
        renderer: partial(classifierLogLinkRenderer, showClassifierLogDialog),
    };
};

/**
 * Returns column configuration for monitored infrastructure table.
 * Includes classifier log link renderer in the polling status column.
 *
 * @param {Function} showClassifierLogDialog - Callback to show classifier log dialog
 * @returns {Array} Column configuration array
 */
export const monitoredInfrastructureColumns = (showClassifierLogDialog) => [
    ...MONITORED_INFRASTRUCTURE_COLUMNS_CONFIG.slice(0, 3),
    getPollingStatusColumn(showClassifierLogDialog),
    ...MONITORED_INFRASTRUCTURE_COLUMNS_CONFIG.slice(3),
];

/**
 * Column configuration for virtual infrastructure table.
 * Displays vCenters, Hypervisors, and Virtual Machines with their relationships.
 */
export const VIRTUAL_INFRASTRUCTURE_COLUMNS = [
    COMMON_COLUMNS.nameVirtual,
    COMMON_COLUMNS.ipAddressVirtual,
    COMMON_COLUMNS.collectorNarrow,
    COMMON_COLUMNS.status,
    COMMON_COLUMNS.subType,
    VIRTUAL_SPECIFIC_COLUMNS.vCenter,
    VIRTUAL_SPECIFIC_COLUMNS.dataCenter,
    VIRTUAL_SPECIFIC_COLUMNS.cluster,
    VIRTUAL_SPECIFIC_COLUMNS.hypervisor,
    COMMON_COLUMNS.description,
];

/**
 * Column configuration for infrastructure list selectors.
 * Used in HypervisorListSelector, VirtualMachineListSelector, and InfrastructureListSelector.
 * Provides a simplified view with essential fields only.
 */
export const INFRASTRUCTURE_LIST_COLUMNS = [
    COMMON_COLUMNS.nameList,
    COMMON_COLUMNS.ipAddressVirtual,
    COMMON_COLUMNS.status,
    COMMON_COLUMNS.description,
];
